import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import moment, { Moment } from 'moment'
import { isEmpty } from 'underscore'
import { message } from 'antd'
import { IPlotVariable } from '../../../../types/analytics'
import { DT0Format } from '../../../../utils/StaticVariable'
import { IBestDataset } from '../../../../types/analytics'
import { IEnrichedMetadata } from '../../../../types/metadata'
import {
  IGetBasisChartV2ApiPayload,
  IGetJointScatterPlotV2ApiPayload,
} from '../../../../types/chart'
import { formatDate, getPointNameAndAcronym } from '../../../../utils/common'
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput'
import Api from '../../../../api/Api'
import { MarkerItem } from '../../../../types/home'
import useDatasetStore from "../../../../store/datasetStore"
import {SplitCoordinates} from "../../../../utils/coordinate";

interface UseScatterPlotParams {
  coordinates: number[]
  previewDatasetId: string
  previewDatasetTimeRange: [moment.Moment, moment.Moment]
  categoryDataset: IBestDataset[]
  onDatasetChange: (item: string) => void
  defaultValueShortName?: string
  defaultDirectionShortName?: string
  enrichedMetadata: IEnrichedMetadata[]
  variables: IPlotVariable[]
  dateRange: string[]
  tsData: { [key: string]: any }[]
  onUpdateScatterPlot: (values: IGetBasisChartV2ApiPayload) => void
  onUpdateJointScatterPlot: (values: IGetJointScatterPlotV2ApiPayload) => void
  updatedScatterPlotData: any[]
  pointProps: string
  pointName: string
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  point?: MarkerItem
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
}

export const useScatterPlotParams = (params: UseScatterPlotParams) => {
  const {
    coordinates,
    previewDatasetId,
    previewDatasetTimeRange,
    categoryDataset,
    onDatasetChange,
    defaultValueShortName,
    defaultDirectionShortName,
    enrichedMetadata,
    variables,
    dateRange,
    tsData,
    onUpdateScatterPlot,
    onUpdateJointScatterPlot,
    updatedScatterPlotData,
    pointProps,
    userInput,
    updateUserInput,
    point,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics
  } = params

  const datasetBriefs = useDatasetStore(state => state.datasetBriefs)
  const selfTriggered = useRef(false)

  const [datasetY, setDatasetY] = useState(userInput.ScatterPlot.datasetY)
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.ScatterPlot.variableX
  )
  const [variableY, setVariableY] = useState<IPlotVariable | null>(
    userInput.ScatterPlot.variableY
  )
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([])
  const [variablesY, setVariablesY] = useState<IPlotVariable[]>(userInput.ScatterPlot.variablesY ?? [])
  const [seriesX, setSeriesX] = useState({ name: '', max: 0 })
  const [seriesY, setSeriesY] = useState({ name: '', max: 0 })
  const [plotData, setPlotData] = useState<{ x: number; y: number }[]>([])
  const [readyToUpdate, setReadyToUpdate] = useState(false)
  const [startDate, setStartDate] = useState(userInput?.ScatterPlot?.startDate ?? moment(dateRange[1]).subtract(1, 'years'))
  const [endDate, setEndDate] = useState(userInput?.ScatterPlot?.endDate ?? moment(dateRange[1]))
  const [previewsettingChanged, setPreviewsettingChanged] = useState(false)
  const [warningSameVariable, setWarningSameVariable] = useState(false)
  const [downloadName, setDownloadName] = useState<string | undefined>()

  const doStartDateChange = useCallback((value: any) => {
    if (value) {
      setPreviewsettingChanged(true)
      setStartDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([value, endDate])
      }
    }
  }, [endDate, isForAllAnalytics, setGlobalDateRange])

  const doEndDateChange = useCallback((value: any) => {
    if (value) {
      setPreviewsettingChanged(true)
      setEndDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([startDate, value])
      }
    }
  }, [startDate, isForAllAnalytics, setGlobalDateRange])

  const doDatasetXChange = useCallback((active: string) => {
    onDatasetChange(active)
  }, [onDatasetChange])

  const doDatasetYChange = useCallback(async (active: string) => {
    if (!enrichedMetadata) return

    selfTriggered.current = true

    let metadata = enrichedMetadata.find(
      (md: IEnrichedMetadata) =>
        md.DatasetId === active &&
        md.CoordinatesString === coordinates.join()
    )

    if (!metadata) {
      try {
        setReadyToUpdate(false)
        metadata = await Api.metadata.getMetadata(active)
      } finally {
        setReadyToUpdate(true)
      }
    }

    if (!metadata) return

    const withoutTime = metadata.ItemsInfo.filter(
      (item: IPlotVariable) => item.Name !== 'Time'
    )
    setPreviewsettingChanged(true)
    setVariablesY(withoutTime)
    setVariableY(withoutTime.length > 0 ? withoutTime[0] : null)
    setDatasetY(active)
  }, [enrichedMetadata, coordinates])

  const doVariableChange = useCallback((active: number, xy: 'X' | 'Y') => {
    if (xy === 'X' && variablesX.length > 0) {
      selfTriggered.current = true
      const item = variablesX.find((a) => a.Index === active)!
      if (item === variableY) {
        setWarningSameVariable(true)
      } else {
        setPreviewsettingChanged(true)
        setVariableX(item)
      }
    }

    if (xy === 'Y' && variablesY.length > 0) {
      selfTriggered.current = true
      const item = variablesY.find((a) => a.Index === active)!
      if (item === variableX) {
        setWarningSameVariable(true)
      } else {
        setPreviewsettingChanged(true)
        setVariableY(item)
      }
    }
  }, [variablesX, variablesY, variableX, variableY])

  const handleCheckboxChange = useCallback(() => {
    selfTriggered.current = true
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics)
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics])

  const drawTable = useCallback((
    data: any[],
    dataKeyX: string,
    dataKeyY: string,
    variableX: IPlotVariable,
    variableY: IPlotVariable
  ) => {
    const filterData: { [key: number]: number[] } = {}
    const xValues = data.map(x => parseFloat(x[dataKeyX]))
    const yValues = data.map(x => parseFloat(x[dataKeyY]))
    let xMax = 0
    let yMax = 0

    for (let index = 0; index < data.length; index++) {
      const x = xValues[index]
      const y = yValues[index]
      yMax = y > yMax ? y : yMax
      xMax = x > xMax ? x : xMax
      if (filterData[x] === undefined) {
        filterData[x] = [y]
      } else if (!filterData[x].includes(y)) {
        filterData[x].push(y)
      }
    }

    const newPlotData = Object.keys(filterData).map((k) => {
      const emptyData: { [key: string]: number } = {}
      const numK = parseFloat(k)
      const filterDataForNumK = filterData[numK]
      if (filterDataForNumK === undefined) {
        return emptyData
      } else {
        const data = filterDataForNumK.reduce(
          (a, b, i) => {
            return { ...a, [`y${i}`]: b }
          },
          { x: numK }
        )
        return data
      }
    })

    setSeriesX({
      name: `${variableX.Name} [${variableX.Unit}]`,
      max: xMax * 1.2,
    })
    setSeriesY({
      name: `${variableY.Name} [${variableY.Unit}]`,
      max: yMax * 1.2,
    })

    setTimeout(() => {
      setPlotData(
        newPlotData === null
          ? newPlotData
          : (newPlotData.sort((a, b) => a.x - b.x) as any)
      )
    }, 500)
  }, [])

  const onUpdateScatterPlotData = useCallback((dateRange?: any) => {
    console.log('datasetY', datasetY)
    console.log('varY', variableY)
    if (previewDatasetId && datasetY && variableX && variableY) {
      if (previewDatasetId === datasetY) {
        setDownloadName(
          getPointNameAndAcronym(previewDatasetId, datasetBriefs, point) +
          pointProps +
          '_' +
          formatDate(startDate) +
          '_' +
          formatDate(endDate) +
          '_Scatter_Plot'
        )
        const values: IGetBasisChartV2ApiPayload = {
          datasetName: previewDatasetId,
          parameters: variableX.Name + '|' + variableY.Name,
          startDate: dateRange ? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
          endDate: dateRange ? dateRange[1].format(DT0Format) : endDate.format(DT0Format),
        }
        onUpdateScatterPlot(values)
      } else {
        setDownloadName(
          getPointNameAndAcronym(previewDatasetId, datasetBriefs, point, datasetY) +
          pointProps +
          '_' +
          formatDate(startDate) +
          '_' +
          formatDate(endDate) +
          '_Scatter_Plot'
        )
        const values: IGetJointScatterPlotV2ApiPayload = {
          datasetName: previewDatasetId,
          datasetName2: datasetY,
          parameters: variableX.Name,
          parameters2: variableY.Name,
          startDate: startDate.format(DT0Format),
          endDate: endDate.format(DT0Format),
        }
        onUpdateJointScatterPlot(values)
      }
    }
  }, [
    startDate,
    endDate,
    previewDatasetId,
    datasetY,
    variableX,
    variableY,
    onUpdateScatterPlot,
    onUpdateJointScatterPlot,
    datasetBriefs,
    point,
    pointProps
  ])

  const disabledStartDate = useCallback((current: Moment) => {
    const isAfterEndDate = current && current > endDate
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(previewDatasetTimeRange[0], 'day')
  }, [endDate, previewDatasetTimeRange])

  const disabledEndDate = useCallback((current: Moment) => {
    const isBeforeStartDate = current && current < startDate
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(previewDatasetTimeRange[1], 'day')
  }, [startDate, previewDatasetTimeRange])

  const periodGreater10years = useCallback(() => {
    const years = endDate.diff(startDate, 'years')
    return years >= 10
  }, [startDate, endDate])

  const memoCoords = useMemo(() => {
    return SplitCoordinates(pointProps)
  }, [pointProps])

  const categories = useMemo(() => {
    const label = categoryDataset.map((bd: IBestDataset) => bd.label)
    return Array.from(new Set(label))
  }, [categoryDataset])

  const dataPreformanceWarning = useMemo(() => {
    return periodGreater10years()
  }, [periodGreater10years])

  const selectedDatasetInfo = useMemo(() => {
    return categoryDataset.find((d) => d.id === previewDatasetId)
  }, [categoryDataset, previewDatasetId])

  useEffect(() => {
    updateUserInput({
      ScatterPlot: {
        datasetX: previewDatasetId,
        datasetY: datasetY,
        variableX: variableX,
        variableY: variableY,
        variablesY: variablesY,
        startDate: startDate,
        endDate: endDate,
      },
    })
  }, [previewDatasetId, datasetY, variableX, variableY, startDate, endDate, updateUserInput, variablesY])

  useEffect(() => {
    if (!previewsettingChanged && dateRange && dateRange.length > 1) {
      setDownloadName(
        getPointNameAndAcronym(previewDatasetId, datasetBriefs, point) +
        pointProps +
        '_' +
        formatDate(moment(dateRange[0])) +
        '_' +
        formatDate(moment(dateRange[1])) +
        '_Scatter_Plot'
      )
    }
  }, [
    dateRange,
    previewsettingChanged,
    pointProps,
    previewDatasetId,
    datasetBriefs,
    point
  ])

  useEffect(() => {
    if(!datasetY){
      setDatasetY(previewDatasetId)
    }
  }, [previewDatasetId, datasetY])

  useEffect(() => {
    if (!isEmpty(variables)) {
      const variableList = variables.filter((item) => item.Name !== 'Time')
      const variablesListY = variablesY.length > 0 ? variablesY : variableList

      setVariablesX(variableList)
      setVariablesY(variablesListY)
      if (
        updatedScatterPlotData.length === 0 &&
        defaultValueShortName &&
        defaultDirectionShortName &&
        variableList.length > 0
      ) {
        const defaultVariableX = variableList.find(
          (v: IPlotVariable) => v.ShortName === defaultValueShortName
        )
        defaultVariableX !== undefined
          ? setVariableX(defaultVariableX)
          : setVariableX(variableList[0])
        const defaultVariableY = variablesListY.find(
          (v: IPlotVariable) => v.ShortName === defaultDirectionShortName
        )
        defaultVariableY !== undefined
          ? setVariableY(defaultVariableY)
          : setVariableY(variablesListY[1])
      }
    }
  }, [variables, defaultValueShortName, defaultDirectionShortName, updatedScatterPlotData, previewDatasetId])

  useEffect(() => {
    previewsettingChanged &&
    message.warn('Please click Update once you finished your selection')
  }, [previewsettingChanged])

  useEffect(() => {
    if (warningSameVariable) {
      message.warn('Cannot use the same variable for both X and Y axes')
      setWarningSameVariable(false)
    }
  }, [warningSameVariable])

  useEffect(() => {
    if (
      updatedScatterPlotData.length === 0 &&
      !previewsettingChanged &&
      variableX !== undefined &&
      variableY !== undefined
    ) {
      const dataKeyX = variableX?.Name.split(' ').join('_') || ''
      const dataKeyY = variableY?.Name.split(' ').join('_') || ''

      const firstTSData = tsData && tsData[0] ? tsData[0] : null
      if (
        variableX &&
        variableY &&
        firstTSData &&
        dataKeyX in firstTSData &&
        dataKeyY in firstTSData
      ) {
        drawTable(tsData, dataKeyX, dataKeyY, variableX, variableY)
      }
    }
  }, [variableX, variableY, tsData, previewsettingChanged, updatedScatterPlotData])

  useEffect(() => {
    if (isForAllAnalytics && datasetY && variableX && variableY) {
      if (selfTriggered.current) {
        selfTriggered.current = false
        return
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateScatterPlotData(globalDateRange)
        setStartDate(globalDateRange[0])
        setEndDate(globalDateRange[1])
      }
    }
  }, [isForAllAnalytics, datasetY, variableX, variableY, globalDateRange])

  useEffect(() => {
    if (
      variableX !== undefined &&
      variableY !== undefined &&
      updatedScatterPlotData.length > 0
    ) {
      let dataKeyX = variableX?.Name.split(' ').join('_') || ''
      let dataKeyY = variableY?.Name.split(' ').join('_') || ''

      if (dataKeyX === dataKeyY && previewDatasetId !== datasetY) {
        dataKeyX = `${dataKeyX}_X`
        dataKeyY = `${dataKeyY}_Y`
      }

      const firstTSData =
        updatedScatterPlotData && updatedScatterPlotData[0]
          ? updatedScatterPlotData[0]
          : null

      if (
        variableX &&
        variableY &&
        firstTSData &&
        dataKeyX in firstTSData &&
        dataKeyY in firstTSData
      ) {
        drawTable(
          updatedScatterPlotData,
          dataKeyX,
          dataKeyY,
          variableX,
          variableY
        )
      }
    }
  }, [variableX, variableY, updatedScatterPlotData, previewDatasetId, datasetY, drawTable])

  return {
    previewDatasetId,
    datasetY,
    variableX,
    variableY,
    variablesX,
    variablesY,
    seriesX,
    seriesY,
    plotData,
    readyToUpdate,
    startDate,
    endDate,
    downloadName,
    memoCoords,
    categories,
    dataPreformanceWarning,
    selectedDatasetInfo,
    doStartDateChange,
    doEndDateChange,
    doDatasetXChange,
    doDatasetYChange,
    doVariableChange,
    handleCheckboxChange,
    onUpdateScatterPlotData,
    disabledStartDate,
    disabledEndDate,
  }
}
